import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from '@/components/ui/sheet';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Router as RouterIcon, Users as UsersIcon, Layers, AlertTriangle } from 'lucide-react';
import api, { formatApiError } from '@/lib/api';

const EMPTY = { name: '', description: '', nas_ids: [] };

export default function RadiusNasGroups({ onChanged }) {
  const [items, setItems] = useState([]);
  const [nas, setNas] = useState([]);
  const [dbOnline, setDbOnline] = useState(true);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [delGroup, setDelGroup] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [g, n] = await Promise.all([
        api.get('/radius/nas-groups'),
        api.get('/radius/nas').catch(() => ({ data: { items: [] } })),
      ]);
      setItems(g.data.items || []);
      setDbOnline(g.data.db_online !== false);
      setNas(n.data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const refresh = () => { load(); onChanged && onChanged(); };

  const openAdd = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ name: it.name, description: it.description || '', nas_ids: [...(it.nas_ids || [])] }); setOpen(true); };

  const toggleNas = (id) => setForm((f) => ({
    ...f,
    nas_ids: f.nas_ids.includes(id) ? f.nas_ids.filter((x) => x !== id) : [...f.nas_ids, id],
  }));

  const save = async () => {
    if (!form.name.trim()) return toast.error('Nama grup wajib diisi');
    setSaving(true);
    try {
      let res;
      if (editing) res = await api.put(`/radius/nas-groups/${editing.id}`, { name: form.name, description: form.description, nas_ids: form.nas_ids });
      else res = await api.post('/radius/nas-groups', { name: form.name, description: form.description, nas_ids: form.nas_ids });
      if (res?.data?.warning) toast.warning(res.data.warning);
      toast.success(editing ? 'Grup NAS diperbarui' : 'Grup NAS dibuat');
      setOpen(false); refresh();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const doDelete = async () => {
    try {
      const res = await api.delete(`/radius/nas-groups/${delGroup.id}`);
      if (res?.data?.warning) toast.warning(res.data.warning);
      toast.success('Grup NAS dihapus'); setDelGroup(null); refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-3" data-testid="radius-nasgroups-tab">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-muted-foreground max-w-2xl">
          Grup NAS mengelompokkan perangkat NAS/MikroTik. Batasi user RADIUS agar hanya bisa autentikasi
          pada NAS di grup tertentu (lihat tombol <span className="font-medium">NAS Access</span> di tab RADIUS Users).
        </p>
        <Button onClick={openAdd} data-testid="radius-nasgroup-add"><Plus className="w-4 h-4 mr-1.5" /> Add NAS Group</Button>
      </div>

      {!dbOnline && (
        <div className="flex items-start gap-2 text-xs rounded-md border border-amber-500/30 bg-amber-500/10 text-amber-600 dark:text-amber-300 px-3 py-2" data-testid="radius-nasgroup-db-warning">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>DB RADIUS belum terhubung. Grup tetap bisa dibuat & disimpan di Portal, namun penerapan pembatasan ke FreeRADIUS baru aktif saat koneksi DB tersedia.</span>
        </div>
      )}

      <Card className="border-border"><CardContent className="p-3">
        <div className="border border-border rounded-md overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="bg-muted/40 hover:bg-muted/40">{['Nama Grup', 'NAS Anggota', 'User Dibatasi', 'Deskripsi', 'Action'].map((h) => <TableHead key={h} className="text-xs whitespace-nowrap">{h}</TableHead>)}</TableRow></TableHeader>
            <TableBody>
              {loading && (<TableRow><TableCell colSpan={5} className="text-center py-8 text-sm text-muted-foreground">Memuat…</TableCell></TableRow>)}
              {!loading && items.length === 0 && (<TableRow><TableCell colSpan={5} className="text-center py-10 text-sm text-muted-foreground">Belum ada grup NAS. Klik “Add NAS Group”.</TableCell></TableRow>)}
              {!loading && items.map((it) => (
                <TableRow key={it.id} data-testid={`radius-nasgroup-row-${it.id}`}>
                  <TableCell className="text-sm font-medium"><span className="inline-flex items-center gap-1.5"><Layers className="w-3.5 h-3.5 text-sky-500" />{it.name}</span></TableCell>
                  <TableCell className="text-xs">
                    <div className="flex flex-wrap gap-1 max-w-md">
                      {(it.members || []).length === 0 && <span className="text-muted-foreground">— kosong —</span>}
                      {(it.members || []).slice(0, 6).map((m) => (
                        <Badge key={m.id} variant="outline" className="text-[10px] font-mono">{m.shortname || m.nasname}</Badge>
                      ))}
                      {(it.members || []).length > 6 && <span className="text-muted-foreground">+{it.members.length - 6}</span>}
                    </div>
                  </TableCell>
                  <TableCell className="text-xs"><span className="inline-flex items-center gap-1 text-muted-foreground"><UsersIcon className="w-3 h-3" /> {it.user_count ?? 0}</span></TableCell>
                  <TableCell className="text-xs text-muted-foreground">{it.description || '—'}</TableCell>
                  <TableCell><div className="flex items-center gap-1">
                    <Button size="sm" variant="ghost" className="h-8" onClick={() => openEdit(it)} data-testid={`radius-nasgroup-edit-${it.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                    <Button size="sm" variant="ghost" className="h-8 text-rose-500" onClick={() => setDelGroup(it)} data-testid={`radius-nasgroup-del-${it.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent></Card>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="radius-nasgroup-form">
          <SheetHeader>
            <SheetTitle>{editing ? 'Edit NAS Group' : 'Add NAS Group'}</SheetTitle>
            <SheetDescription>Pilih NAS/MikroTik yang menjadi anggota grup ini.</SheetDescription>
          </SheetHeader>
          <div className="space-y-3 mt-4">
            <div><Label className="text-xs">Nama Grup</Label><div className="mt-1"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="POP Jakarta Selatan" data-testid="radius-nasgroup-f-name" /></div></div>
            <div><Label className="text-xs">Deskripsi</Label><div className="mt-1"><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="NAS untuk area tertentu" data-testid="radius-nasgroup-f-desc" /></div></div>
            <div>
              <Label className="text-xs flex items-center justify-between">
                <span className="inline-flex items-center gap-1"><RouterIcon className="w-3.5 h-3.5" /> Anggota NAS / MikroTik</span>
                <span className="text-muted-foreground">{form.nas_ids.length} dipilih</span>
              </Label>
              <div className="mt-1 border border-border rounded-md max-h-72 overflow-y-auto divide-y divide-border" data-testid="radius-nasgroup-f-naslist">
                {nas.length === 0 && <div className="text-xs text-muted-foreground px-3 py-4 text-center">Belum ada NAS terdaftar (atau DB RADIUS belum terhubung). Tambahkan NAS di tab “NAS / MikroTik”.</div>}
                {nas.map((n) => (
                  <label key={n.id} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-muted/40" data-testid={`radius-nasgroup-f-nas-${n.id}`}>
                    <Checkbox checked={form.nas_ids.includes(n.id)} onCheckedChange={() => toggleNas(n.id)} />
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{n.shortname}</div>
                      <div className="text-[11px] text-muted-foreground font-mono truncate">{n.nasname} · {n.type}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <SheetFooter className="mt-4"><Button variant="outline" onClick={() => setOpen(false)}>Batal</Button><Button onClick={save} disabled={saving} data-testid="radius-nasgroup-save">{saving ? 'Menyimpan…' : 'Simpan'}</Button></SheetFooter>
        </SheetContent>
      </Sheet>

      <AlertDialog open={!!delGroup} onOpenChange={(o) => !o && setDelGroup(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Hapus Grup NAS?</AlertDialogTitle><AlertDialogDescription>Grup <span className="font-mono">{delGroup?.name}</span> akan dihapus. User yang dibatasi ke grup ini akan otomatis dilepas dari grup tersebut.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Batal</AlertDialogCancel><AlertDialogAction className="bg-rose-600 hover:bg-rose-700" onClick={doDelete} data-testid="radius-nasgroup-del-confirm">Ya, Hapus</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
